"use client";

/**
 * `MoneyField` — currency-aware numeric input. The control itself is a
 * native `<input type="number">` that registers with rhf using
 * `valueAsNumber: true`; the canonical form-state value is therefore a
 * `number` in the major unit (e.g. `12.50` for €12.50). Display
 * formatting (thousands separators, locale-specific decimals) belongs to
 * the surrounding {@link Form} composer's `formatters` bundle and is
 * applied at render boundaries — this field stays canonical so zod
 * refinements, totals, and tax math operate on raw numbers.
 *
 * Visual layout: a flex row with the input on the left and the currency
 * code (`field.currency`) rendered as a suffix label on the right. The
 * suffix sits inside a same-height bordered span so the combined control
 * looks like one unit. We use `aria-hidden` on the suffix because the
 * accessible name comes from `<Label>` via `FieldShell`; screen readers
 * already speak "Price, EUR" when callers compose the label as e.g.
 * `"Price (EUR)"`. Including the suffix in the a11y tree would
 * double-announce the currency on most readers.
 *
 * Step defaults to `0.01` so users can enter cents/para directly. Pass
 * an explicit `step` on a future field option if a coarser increment is
 * required (e.g. invoice rounding to the nearest dinar).
 *
 * See `number-field.tsx` for the rationale on `valueAsNumber`, the empty-
 * input → NaN edge, and why we don't normalise inside the field.
 */

import * as React from "react";
import { Input } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { MoneyFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface MoneyFieldProps {
  field: MoneyFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function MoneyField({
  field,
  pathPrefix,
}: MoneyFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <div className="flex items-stretch gap-0">
        <Input
          id={path}
          type="number"
          step="0.01"
          inputMode="decimal"
          aria-invalid={errorMessage !== undefined ? true : undefined}
          className="rounded-r-none"
          {...(field.disabled ? { disabled: true } : {})}
          {...(field.placeholder !== undefined
            ? { placeholder: field.placeholder }
            : {})}
          {...form.register(path, { valueAsNumber: true })}
        />
        <span
          aria-hidden="true"
          className="inline-flex h-[var(--aio-input-height,2.75rem)] items-center justify-center rounded-r-md border border-l-0 border-border bg-muted px-3 text-sm text-fg-muted"
        >
          {field.currency}
        </span>
      </div>
    </FieldShell>
  );
}
